import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useGetServiceTypesQuery } from '../../../services/redux/api';
import type { ServiceType } from '../../../services/redux/api/serviceType.api';
import { formatServiceTypeLabel } from '../../../utils/constants/serviceTypes';

interface ServiceTypeSelectProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  required?: boolean;
  error?: string | null;
  disabled?: boolean;
}

/**
 * Custom styled dropdown for IATA service type codes.
 * Fetches data internally via useGetServiceTypesQuery().
 */
export function ServiceTypeSelect({
  value,
  onChange,
  onBlur,
  required,
  error,
  disabled,
}: ServiceTypeSelectProps) {
  const { data: serviceTypes = [] } = useGetServiceTypesQuery();

  const activeTypes = useMemo(
    () => serviceTypes.filter(st => st.isActive).sort((a, b) => a.code.localeCompare(b.code)),
    [serviceTypes],
  );

  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const justSelectedRef = useRef(false);

  const hasError = error && error.trim() !== '';

  const selectedType = useMemo(
    () => activeTypes.find(st => st.code === value),
    [activeTypes, value],
  );

  // Position dropdown via portal
  useEffect(() => {
    if (showDropdown && triggerRef.current) {
      const updatePosition = () => {
        if (triggerRef.current) {
          const rect = triggerRef.current.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width,
          });
        }
      };

      updatePosition();

      const handleScroll = (e: Event) => {
        if (dropdownRef.current && dropdownRef.current.contains(e.target as Node)) return;
        setShowDropdown(false);
      };

      const handleResize = () => setShowDropdown(false);

      const handleClickOutside = (e: MouseEvent) => {
        if (
          triggerRef.current && !triggerRef.current.contains(e.target as Node) &&
          dropdownRef.current && !dropdownRef.current.contains(e.target as Node)
        ) {
          setShowDropdown(false);
        }
      };

      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDropdown]);

  // Reset selectedIndex when dropdown closes
  useEffect(() => {
    if (!showDropdown) {
      setSelectedIndex(-1);
    }
  }, [showDropdown]);

  const handleSelect = (st: ServiceType) => {
    justSelectedRef.current = true;
    onChange(st.code);
    setShowDropdown(false);
  };

  const toggleDropdown = () => {
    if (!disabled) setShowDropdown(prev => !prev);
  };

  const handleBlur = () => {
    setTimeout(() => {
      setShowDropdown(false);
      // Skip onBlur if a selection was just made — onChange already handled validation
      if (justSelectedRef.current) {
        justSelectedRef.current = false;
        return;
      }
      onBlur?.();
    }, 200);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!showDropdown) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setShowDropdown(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, activeTypes.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < activeTypes.length) {
          handleSelect(activeTypes[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        triggerRef.current?.focus();
        break;
    }
  };

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && dropdownRef.current) {
      const item = dropdownRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  const renderDropdown = () => {
    if (!showDropdown) return null;

    const dropdown = (
      <div
        ref={dropdownRef}
        style={{
          position: 'fixed',
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${dropdownPosition.width}px`,
          maxHeight: '260px',
          overflowY: 'auto',
          overflowX: 'hidden',
          zIndex: 9999,
        }}
        className="bg-white border border-gray-200 rounded-lg shadow-xl"
      >
        {activeTypes.length === 0 && (
          <div className="px-3 py-2.5 text-xs text-gray-400">No service types available</div>
        )}

        {activeTypes.length > 0 && (
          <ul className="py-1">
            {activeTypes.map((st, index) => (
              <li key={st.code} data-index={index}>
                <button
                  type="button"
                  onClick={() => handleSelect(st)}
                  className={`w-full px-3 py-2 text-left transition-colors
                    ${index === selectedIndex ? 'bg-primary-50' : 'hover:bg-gray-50'}
                    ${st.code === value ? 'ring-1 ring-inset ring-primary-200' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`font-bold text-sm font-mono w-5 flex-shrink-0 ${index === selectedIndex ? 'text-primary-700' : 'text-gray-900'}`}
                    >
                      {st.code}
                    </span>
                    <span className={`text-sm truncate ${index === selectedIndex ? 'text-primary-600' : 'text-gray-600'}`}>
                      {st.description}
                    </span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );

    return createPortal(dropdown, document.body);
  };

  return (
    <label className="block mb-2 min-w-0">
      <span className="block mb-1.5 text-base font-semibold text-gray-600">
        Service Type
        {required && <span className="text-danger-500 ml-1">*</span>}
      </span>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggleDropdown}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`w-full min-w-0 px-3 py-2.5 border rounded-lg text-lg outline-none transition-all text-left block truncate
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-pointer'}
          ${hasError
            ? 'border-danger-500 focus:border-danger-500 focus:ring-3 focus:ring-danger-500/10'
            : 'border-gray-300 focus:border-primary-500 focus:ring-3 focus:ring-primary-500/10'
          }
          ${selectedType ? 'text-gray-900' : 'text-gray-400'}`}
      >
        {selectedType ? formatServiceTypeLabel(selectedType) : 'Select service type'}
      </button>

      {renderDropdown()}

    </label>
  );
}
